import React from 'react';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data/products'; // <-- Add this import

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  productData = null,
  breadcrumbs = null,
  price = null,
  availability = 'InStock'
}) => {
  const siteTitle = "Shree Balaji Corporation";
  const baseUrl = "https://shreebalajicorporation.com"; // Replace with your actual domain
  const fullTitle = title ? `${title} | ${siteTitle}` : `${siteTitle} - Leading Supplier of PET Straps, Bale Covers & Strapping Machines Since 1970`;
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const defaultImage = `${baseUrl}/assets/logo-social.png`;
  const seoImage = image ? `${baseUrl}${image}` : defaultImage;

  // Organization structured data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Shree Balaji Corporation",
    "description": "Leading supplier of PET straps, bale covers, leather washers, and strapping machines for cotton ginning and pressing industries since 1970.",
    "url": baseUrl,
    "logo": `${baseUrl}/assets/logo.png`,
    "foundingDate": "1970",
    "industry": "Industrial Packaging",
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+91-9422161161",
        "contactType": "customer service",
        "availableLanguage": ["English", "Hindi", "Marathi"],
        "areaServed": "IN"
      },
      {
        "@type": "ContactPoint",
        "telephone": "+91-9764166071",
        "contactType": "sales",
        "availableLanguage": ["English", "Hindi", "Marathi"],
        "areaServed": "IN"
      }
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "50, Midc Phase 3 and 4, MIDC",
      "addressLocality": "Akola",
      "addressRegion": "Maharashtra",
      "postalCode": "444104",
      "addressCountry": "IN"
    },
    "sameAs": [
      // Add your social media URLs here
    ],
    "makesOffer": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Product",
          "name": "PET Straps for Cotton Bales"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Product",
          "name": "Bale Covers"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Product",
          "name": "Pneumatic Strapping Machines"
        }
      }
    ]
  };

  // Product structured data
  const productSchema = productData ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": productData.name,
    "description": productData.description,
    "image": `${baseUrl}${productData.image}`,
    "brand": {
      "@type": "Brand",
      "name": "Shree Balaji Corporation"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Shree Balaji Corporation"
    },
    "offers": {
      "@type": "Offer",
      "price": productData.price,
      "priceCurrency": "INR",
      "availability": `https://schema.org/${availability}`,
      "seller": {
        "@type": "Organization",
        "name": "Shree Balaji Corporation"
      },
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    },
    "category": productData.categoryName,
    "sku": `SBC-${productData.id}`,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150"
    }
  } : null;

  // Breadcrumb structured data
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${baseUrl}${crumb.url}`
    }))
  } : null;

  const defaultDescription = "Leading supplier of PET straps, bale covers, leather washers, and strapping machines for cotton ginning and pressing industries. Serving Maharashtra and India since 1970.";
  const defaultKeywords = "PET straps, bale covers, leather washers, strapping machines, cotton ginning, pressing industry, industrial packaging, Akola, Maharashtra";

  const metaDescription = description || defaultDescription;
  const metaKeywords = keywords || defaultKeywords;

  // LocalBusiness structured data
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": baseUrl,
    "name": siteTitle,
    "image": defaultImage,
    "description": defaultDescription,
    "url": baseUrl,
    "telephone": [companyInfo.phone, companyInfo.whatsapp],
    "email": companyInfo.email,
    "priceRange": "₹₹",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "50, Midc Phase 3 and 4, MIDC",
      "addressLocality": "Akola",
      "addressRegion": "Maharashtra",
      "postalCode": "444104",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "20.7002",
      "longitude": "77.0082"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ],
      "opens": "09:00",
      "closes": "18:00"
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Shree Balaji Corporation" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="hi_IN" />
      <meta property="og:price:currency" content="INR" />
      {price && <meta property="og:price:amount" content={price} />}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={seoImage} />

      {/* Additional SEO Meta Tags */}
      <meta name="geo.region" content="IN-MH" />
      <meta name="geo.placename" content="Akola, Maharashtra" />
      <meta name="geo.position" content="20.7002;77.0082" />
      <meta name="ICBM" content="20.7002, 77.0082" />
      <meta name="generator" content="React" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="HandheldFriendly" content="true" />
      <meta name="MobileOptimized" content="width" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      <meta name="apple-mobile-web-app-title" content={siteTitle} />
      <meta name="theme-color" content="#21808D" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* Business specific meta tags */}
      <meta name="business:contact_data:locality" content="Akola" />
      <meta name="business:contact_data:region" content="Maharashtra" />
      <meta name="business:contact_data:postal_code" content="444104" />
      <meta name="business:contact_data:country_name" content="India" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
};

export default SEO;
